import { useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useLocation } from "react-router-dom";
import { HELP_TOPICS, topicForPath, type HelpTopic } from "../help/topics";

function TopicBody({ topic }: { topic: HelpTopic }) {
  const tips = [...(topic.tips ?? []), ...(topic.tip ? [topic.tip] : [])];
  return (
    <div className="grid gap-3 px-3 pb-4 pt-1">
      <p className="text-sm text-muted">{topic.intro}</p>

      <ol className="grid gap-2">
        {topic.steps.map((s, i) => (
          <li key={i} className="flex gap-3">
            <span className="mt-0.5 grid h-5 w-5 shrink-0 place-items-center rounded-full bg-accent/15 font-mono text-[11px] font-bold text-accent-strong">{i + 1}</span>
            <span className="text-sm">
              <b className="font-semibold">{s.title}.</b> <span className="text-muted">{s.detail}</span>
            </span>
          </li>
        ))}
      </ol>

      {topic.fields && topic.fields.length > 0 && (
        <div className="rounded-lg border border-line bg-surface-2 p-3">
          <p className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-widest text-muted">Fields explained</p>
          <ul className="grid gap-1.5">
            {topic.fields.map((f) => (
              <li key={f.name} className="text-[13px]">
                <span className="font-semibold">{f.name}</span>
                {f.required
                  ? <span className="ml-1.5 rounded bg-danger/10 px-1 py-0.5 font-mono text-[9px] font-bold uppercase text-danger">Required</span>
                  : <span className="ml-1.5 rounded bg-surface px-1 py-0.5 font-mono text-[9px] font-bold uppercase text-muted">Optional</span>}
                <span className="text-muted"> — {f.note}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {topic.useCases && topic.useCases.length > 0 && (
        <div className="grid gap-1.5">
          <p className="px-1 font-mono text-[10px] font-bold uppercase tracking-widest text-muted">Common tasks</p>
          <ul className="grid gap-1.5">
            {topic.useCases.map((u, i) => (
              <li key={i} className="rounded-lg border border-line px-3 py-2 text-[13px]">
                <b className="font-semibold">{u.title}</b> <span className="text-muted">— {u.detail}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      {topic.faqs && topic.faqs.length > 0 && (
        <div className="grid gap-1.5">
          <p className="px-1 font-mono text-[10px] font-bold uppercase tracking-widest text-muted">Good to know</p>
          <ul className="grid gap-2">
            {topic.faqs.map((f, i) => (
              <li key={i} className="text-[13px]">
                <p className="font-semibold">{f.q}</p>
                <p className="text-muted">{f.a}</p>
              </li>
            ))}
          </ul>
        </div>
      )}

      {tips.length > 0 && (
        <ul className="grid gap-1.5">
          {tips.map((t, i) => (
            <li key={i} className="rounded-lg border border-verified/30 bg-verified-bg/40 px-3 py-2 text-[13px] text-muted">
              💡 <span className="text-ink">{t}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

/** Slide-out help drawer: the contextual topic for the current page is shown first + expanded,
 *  with every topic browsable below. Content is authored in src/help/topics.ts. */
export function HelpPanel({ onClose }: { onClose: () => void }) {
  const { pathname } = useLocation();
  const contextual = topicForPath(pathname);
  const [openKey, setOpenKey] = useState<string | null>(contextual?.key ?? null);
  const [query, setQuery] = useState("");

  const q = query.trim().toLowerCase();
  // When searching, rank across every topic's text; otherwise show contextual first.
  const ordered = useMemo(() => {
    if (q) {
      const hay = (t: HelpTopic) =>
        [t.title, t.intro, ...t.steps.flatMap((s) => [s.title, s.detail]),
         ...(t.fields ?? []).flatMap((f) => [f.name, f.note]),
         ...(t.useCases ?? []).flatMap((u) => [u.title, u.detail]),
         ...(t.faqs ?? []).flatMap((f) => [f.q, f.a]),
         ...(t.tips ?? []), t.tip ?? ""].join(" ").toLowerCase();
      return HELP_TOPICS.filter((t) => q.split(/\s+/).every((w) => hay(t).includes(w)));
    }
    return contextual
      ? [contextual, ...HELP_TOPICS.filter((t) => t.key !== contextual.key)]
      : HELP_TOPICS;
  }, [q, contextual]);

  // Rendered via a portal to <body> so it escapes the sticky/backdrop-blur header's stacking
  // context — otherwise the drawer's z-index is capped below page content and gets covered.
  return createPortal(
    <>
      <div className="fixed inset-0 z-[100] bg-black/50" onClick={onClose} aria-hidden />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Help"
        className="fixed inset-y-0 right-0 z-[101] flex w-full max-w-md flex-col border-l border-line bg-surface shadow-2xl"
      >
        <header className="flex items-center justify-between gap-3 border-b border-line px-4 py-3">
          <h2 className="font-display text-lg font-black tracking-tight">Help</h2>
          <button type="button" onClick={onClose} aria-label="Close help" className="grid h-8 w-8 place-items-center rounded-lg text-muted hover:bg-surface-2 hover:text-ink">✕</button>
        </header>

        <div className="border-b border-line px-3 py-2.5">
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search help — e.g. IFTA, bid, verify, 2FA…"
            aria-label="Search help"
            className="w-full rounded-lg border border-line bg-surface-2 px-3 py-2 text-sm outline-none placeholder:text-muted focus:border-accent"
          />
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto p-3">
          {contextual && !q && (
            <p className="mb-2 px-1 font-mono text-[10px] font-bold uppercase tracking-widest text-accent-strong">Help for this page</p>
          )}
          {q && (
            <p className="mb-2 px-1 text-[12px] text-muted">{ordered.length} {ordered.length === 1 ? "topic" : "topics"} matching “{query.trim()}”</p>
          )}
          <ul className="grid gap-1.5">
            {ordered.map((topic) => {
              const expanded = openKey === topic.key;
              return (
                <li key={topic.key} className="overflow-hidden rounded-lg border border-line">
                  <button
                    type="button"
                    aria-expanded={expanded}
                    onClick={() => setOpenKey(expanded ? null : topic.key)}
                    className={`flex w-full items-center justify-between gap-3 px-3 py-2.5 text-left text-sm font-semibold transition-colors ${expanded ? "bg-surface-2" : "hover:bg-surface-2"}`}
                  >
                    {topic.title}
                    <span className={`text-muted transition-transform ${expanded ? "rotate-90" : ""}`}>›</span>
                  </button>
                  {expanded && <TopicBody topic={topic} />}
                </li>
              );
            })}
          </ul>
          {q && ordered.length === 0 && (
            <p className="px-1 py-6 text-center text-[13px] text-muted">No help topics match “{query.trim()}”. Try a different word, or open a support ticket below.</p>
          )}
          <p className="mt-4 px-1 text-[12px] text-muted">
            Still stuck? Use the feedback button (💡) in the top bar, or open a <a href="/support" className="text-accent-strong hover:underline">support ticket</a>.
          </p>
        </div>
      </div>
    </>,
    document.body,
  );
}
